import React, { useState, useRef } from 'react'
import {
  Button,
  BUTTON_THEME,
  Icon,
  Input,
  Modal,
  TextArea,
} from '@st-operations/uikit/src/v2/index.js'
import format from '@st-operations/uikit/lib/format'

import { createAutoscaling, getAutoscaling } from '_services/autoscaling-processes'
import TopicsList from '../topics-list'

import './styles.scss'

const sizeToNumber = (value) => Number(value.replace(/,/g, ''))

const AutoscaleTopicsModal = ({
  isOpen,
  handleClose,
  audience,
  handleAddTopic,
  handleAddMultipleTopics,
}) => {
  const [size, setSize] = useState('10000000')
  const [description, setDescription] = useState('')
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const intervalRef = useRef(null)

  const handleGetTopics = () => {
    setIsLoading(true)
    createAutoscaling({
      audience,
      description,
      size: sizeToNumber(size),
      name: audience.name,
    }).then((resp) => {
      pollAutoscaleTopics(resp._id)
    })
  }

  const pollAutoscaleTopics = (respID) => {
    if (!respID) return

    setIsLoading(true)

    intervalRef.current = setInterval(() => {
      getAutoscaling(respID)
        .then((resp) => {
          setData(resp)
          if (resp.status === 'finished') {
            clearInterval(intervalRef.current)
            setIsLoading(false)
          }
        })
        .catch((error) => {
          toast.error(error.message)
          clearInterval(intervalRef.current)
          setIsLoading(false)
        })
    }, 500)
  }

  const handleCancel = () => {
    setData(null)
    setIsLoading(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    handleClose()
  }

  const sizeMask = (value) => {
    const number = sizeToNumber(value)
    if (isNaN(number)) return value
    return number.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }

  const filterTopics = (topics) => {
    return topics.filter((topic) => !audience.topics.some((t) => t.name === topic.name))
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="autoscale-topics-modal" size="large">
      <div>
        <h3 className="autoscale-topics-modal__title">
          <Icon width="24px" height="24px" icon="idea" type="outline" color="currentColor" />
          Autoscale Topics
        </h3>
        <p className="autoscale-topics-modal__subtitle">
          By utilizing this feature, you can significantly broaden the range of topics you present,
          allowing you to cater to a wider array of interests and preferences within your audience
        </p>
        <TextArea
          label="Description"
          name="description"
          value={description}
          onChange={(_e, _name, value) => setDescription(value)}
          placeholder="Type your Description"
          className="autoscale-topics-modal__description"
        />
        <Input
          label="Size"
          onChange={(value) => setSize(value)}
          placeholder="Target size"
          value={sizeMask(size)}
        />
        <div className="autoscale-topics-modal__actions">
          <Button onClick={handleCancel} theme={BUTTON_THEME.SECONDARY}>
            Cancel
          </Button>
          <Button onClick={handleGetTopics} disabled={isLoading || data}>
            Start
          </Button>
        </div>
        <If condition={isLoading}>
          <div className="autoscale-topics-modal__progress-bar-wrapper">
            <div
              className="autoscale-topics-modal__progress-bar"
              style={{ '--progress-bar-percentage': data?.progressPercentage ?? 0 }}
            />
          </div>
        </If>
        <If condition={data?.topics?.length > 0}>
          <TopicsList
            title={`Autoscale Topics (${format('metric', data?.outputSize)})`}
            topics={filterTopics(data.topics)}
            handleAddTopic={(topic) => {
              toast.success(`Topic ${topic.name} added`)
              handleAddTopic(topic)
            }}
            handleAddMultipleTopics={(topics) => {
              toast.success(`${topics.length} topics added`)
              handleAddMultipleTopics(topics)
              handleClose()
            }}
          />
        </If>
        <If condition={data?.status === 'finished' && !data?.topics?.length}>
          <p className="autoscale-topics-modal__no-topics">No topics found for this audience</p>
        </If>
      </div>
    </Modal>
  )
}

export default AutoscaleTopicsModal
